import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import type { IFolderFormData } from './types';
import type { IEditFolderModalData } from '@/hooks/types';
import type { ISimpleResourceType } from '@/components/common';

import { formData } from '@/helpers';
import { Button, Checkbox, Input } from '@/components/common';
import { folderEditSchema } from './modalSchemas';

interface IFolderFormProps {
  onSubmit: (data: IFolderFormData) => void;
  onCancel: () => void;
  folderType: ISimpleResourceType;
  data?: IEditFolderModalData;
}

const FolderForm = ({ onSubmit, onCancel, data, folderType }: IFolderFormProps): React.ReactElement => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isDirty },
  } = useForm<IFolderFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(folderEditSchema),
    defaultValues: data,
  });

  useEffect(() => {
    reset(data || { name: '', agentVisible: true });
  }, [data, reset]);

  const formDataOptions = {
    register,
    touchedFields,
    errors,
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Input
        name="name"
        type="text"
        label={t`resources.modals.name`}
        autoFocus
        {...formData('name', formDataOptions)}
      />
      {folderType === 'document' && (
        <Checkbox
          name="agentVisible"
          label={t`resources.modals.agentVisible`}
          {...formData('agentVisible', formDataOptions)}
        />
      )}
      <FormControl>
        <StyledButton color="blueOutline" className="me-3" onClick={onCancel}>
          {t`common.btnCancel`}
        </StyledButton>
        <StyledButton color="blue" type="submit" disabled={!isDirty}>
          {data !== undefined ? t`common.btnSave` : t`common.btnCreate`}
        </StyledButton>
      </FormControl>
    </form>
  );
};

export { FolderForm };

const StyledButton = styled(Button)`
  width: 190px;
`;

const FormControl = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;
